import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash, Command } from '@phosphor-icons/react';

interface DeleteWebpageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webpageTitle: string;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteWebpageDialog({
  open,
  onOpenChange,
  webpageTitle,
  onConfirm,
  isDeleting = false,
}: DeleteWebpageDialogProps) {
  // Keyboard shortcut: CMD+D to confirm delete when dialog is open
  useEffect(() => {
    if (!open || isDeleting) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isTyping) return;
      
      // CMD+D to delete
      if (e.key.toLowerCase() === 'd' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onConfirm();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isDeleting, onConfirm]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash className="h-5 w-5 text-destructive" weight="bold" />
            Delete webpage?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">"{webpageTitle}"</span>?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting} className="gap-2">
            Cancel
            <kbd className="inline-flex h-5 items-center justify-center rounded bg-neutral-100 px-1.5 text-[10px] font-medium text-neutral-600">
              ESC
            </kbd>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
            {!isDeleting && (
              <kbd className="inline-flex h-5 items-center justify-center gap-0.5 rounded bg-destructive-foreground/20 px-1 text-destructive-foreground">
                <Command className="h-3 w-3" weight="bold" />
                <span className="text-[10px] font-medium">D</span>
              </kbd>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

